import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useBlocker } from "react-router-dom";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Progress from "@/components/Progress";
import SegmentedButton from "@/components/SegmentedButton";
import usePageTitle from "@/hooks/usePageTitle";
import { useParams } from "@/hooks/useSearchParams";
import { useErrorBanner } from "@/providers/ErrorBannerProvider/useErrorBanner";
import TabLayout from "../_TabLayout";
import { useInteractionTopicsAccess } from "../access";
import { shouldSwitchConfigurationToLocalBackend } from "../localBackend";
import ConfigurationLoadError from "../_components/ConfigurationLoadError";
import TopicsEditor, { topicFocusKey } from "./_components/TopicsEditor";
import TopicQualityPanel from "./_components/TopicQualityPanel";
import {
  INTERACTION_TOPIC_CONFIGS_QUERY,
  INTERACTION_TOPIC_DEPARTMENT_PARAM,
  INTERACTION_TOPIC_DEPARTMENTS,
  OTHER_TOPIC,
  constrainInteractionTopicDepartment,
  type InteractionSubtopicInput,
  type InteractionTopicConfig,
  type InteractionTopicInput
} from "./queries";
import { UPDATE_INTERACTION_TOPIC_CONFIG_MUTATION, type UpdateInteractionTopicConfigVariables } from "./mutations";
import { ANALYZE_TOPICS_QUERY, SUGGEST_TOPIC_DESCRIPTION_QUERY, type TopicFinding } from "./aiQueries";
import {
  createRowKey,
  diffChangedTopics,
  diffSavedTopics,
  hasValidationErrors,
  isTopicLocked,
  mergeEchoTopics,
  suggestionKeyFor,
  topicsEqual,
  validateInteractionTopics,
  type ChangedTopicFields,
  type TopicFieldChanges
} from "./_utils/topicsValidation";

const SAVE_FLASH_MS = 1800;

function insertBeforeOther<T>(items: T[], item: T, isOther: (value: T) => boolean): T[] {
  const otherIndex = items.findIndex(isOther);
  if (otherIndex === -1) return [...items, item];
  return [...items.slice(0, otherIndex), item, ...items.slice(otherIndex)];
}

function insertionIndexBeforeOther<T>(items: T[], isOther: (value: T) => boolean): number {
  const otherIndex = items.findIndex(isOther);
  return otherIndex === -1 ? items.length : otherIndex;
}

function isOtherName(name: string): boolean {
  return name.trim() === OTHER_TOPIC;
}

// Server lists carry the canonical trailing Other; editing works on the raw
// rows without it (the backend re-appends it on save).
function stripCanonicalOther(subtopics: InteractionSubtopicInput[]): InteractionSubtopicInput[] {
  const rows = [...subtopics];
  if (rows.length > 0 && isOtherName(rows[rows.length - 1].name)) rows.pop();
  return rows;
}

function toEditableTopic(topic: InteractionTopicConfig["topics"][number]): InteractionTopicInput {
  return {
    name: topic.name,
    description: topic.description ?? "",
    subtopics: stripCanonicalOther(topic.subtopics ?? []).map((sub) => ({
      ...sub,
      key: sub.key ?? createRowKey("sub")
    })),
    code: topic.code ?? null,
    defaultPrivate: topic.defaultPrivate ?? null,
    key: topic.key ?? createRowKey("topic")
  };
}

function toSavePayload(topic: InteractionTopicInput): InteractionTopicInput {
  return {
    name: topic.name,
    description: topic.description?.trim() ? topic.description.trim() : null,
    subtopics: topic.subtopics
      .filter((row) => row.name.trim().length > 0)
      .map((row) => ({
        name: row.name.trim(),
        description: row.description?.trim() ? row.description.trim() : null
      })),
    code: topic.code ?? null,
    defaultPrivate: topic.defaultPrivate ?? null
  };
}

export type TopicAnalysis = {
  summary: string;
  findings: TopicFinding[];
  configured: boolean;
};

export default function InteractionTopicsTabPage() {
  usePageTitle("/configuration/interaction-topics", "Member 360 Configuration | Interaction Topics");
  const { loaded, canView, departments } = useInteractionTopicsAccess();
  const { params, addSearchParam } = useParams();
  const { setError, clearError } = useErrorBanner();
  const visibleDepartments = useMemo(
    () => INTERACTION_TOPIC_DEPARTMENTS.filter((dept) => departments.includes(dept.value)),
    [departments]
  );
  const department = useMemo(
    () => constrainInteractionTopicDepartment(params[INTERACTION_TOPIC_DEPARTMENT_PARAM], departments),
    [params, departments]
  );

  const [topicsByDept, setTopicsByDept] = useState<Record<string, InteractionTopicInput[]> | null>(null);
  const [lockVersions, setLockVersions] = useState<Record<string, number>>({});
  const [fieldFlash, setFieldFlash] = useState<{
    key: number;
    dept: string;
    changes: TopicFieldChanges;
    deleted: boolean;
  } | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [suggestingKey, setSuggestingKey] = useState<string | null>(null);
  // Synchronous single-flight guard: state alone can't stop a second click
  // before re-render, so the ref rejects it in the same tick.
  const suggestingRef = useRef<string | null>(null);
  // Monotonic review-request id: only the newest analyze response commits,
  // so out-of-order resolves can't replace fresher findings.
  const analysisEpoch = useRef(0);
  // Local review-loading state: the Apollo flag is global, so it would leak
  // "Analyzing..." onto a department switched to mid-request. The ref guards
  // double-clicks synchronously (no re-render needed) and holds the owning
  // epoch so stale completions can't clear a newer run.
  const [analyzing, setAnalyzing] = useState(false);
  const analyzingRef = useRef<number | null>(null);
  const [analysis, setAnalysis] = useState<TopicAnalysis | null>(null);
  const [analyzedDept, setAnalyzedDept] = useState<string | null>(null);
  // Department-scoped saving state: the bar shows loading only for the
  // department being saved, and Discard stays available elsewhere.
  const [savingDept, setSavingDept] = useState<string | null>(null);
  // Set while a conflict reload is outstanding: the bar stays disabled until
  // fresh server topics are installed (a failed reload shows a retry error
  // and re-enables instead of stranding the user).
  const [reloadingDept, setReloadingDept] = useState<string | null>(null);
  const saveBarRef = useRef<HTMLDivElement | null>(null);
  // Department whose save bar held keyboard focus at save click: focus
  // returns to the editor only when that department is still showing.
  const saveFocusDept = useRef<string | null>(null);
  const savedSnapshot = useRef<Record<string, InteractionTopicInput[]>>({});
  const saveEpoch = useRef(0);
  // Serializes saves so a follow-up save always carries the lock version from
  // the previous save's echo instead of racing it into a false conflict.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const latestTopics = useRef<Record<string, InteractionTopicInput[]> | null>(null);
  const latestDepartment = useRef(department);
  const latestLockVersions = useRef<Record<string, number>>({});

  const skip = !loaded || !canView || shouldSwitchConfigurationToLocalBackend();
  const { data, loading, error, refetch } = useQuery(INTERACTION_TOPIC_CONFIGS_QUERY, {
    skip,
    fetchPolicy: "network-only"
  });

  const [updateTopics] = useMutation(UPDATE_INTERACTION_TOPIC_CONFIG_MUTATION, {
    refetchQueries: [{ query: INTERACTION_TOPIC_CONFIGS_QUERY }]
  });
  const [suggestDescription] = useLazyQuery(SUGGEST_TOPIC_DESCRIPTION_QUERY, {
    fetchPolicy: "network-only"
  });
  const [runAnalysisQuery] = useLazyQuery(ANALYZE_TOPICS_QUERY, {
    fetchPolicy: "network-only"
  });
  latestTopics.current = topicsByDept;
  latestDepartment.current = department;
  latestLockVersions.current = lockVersions;

  useEffect(() => {
    if (data && !topicsByDept) {
      const initial: Record<string, InteractionTopicInput[]> = {};
      const versions: Record<string, number> = {};
      for (const config of data.interactionTopicConfigs) {
        initial[config.department] = config.topics.map(toEditableTopic);
        versions[config.department] = config.lockVersion ?? 0;
      }
      savedSnapshot.current = initial;
      setTopicsByDept(initial);
      setLockVersions(versions);
    }
  }, [data, topicsByDept]);

  useEffect(() => {
    return () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    };
  }, []);

  const currentTopics = useMemo(() => topicsByDept?.[department] ?? [], [topicsByDept, department]);
  const errors = useMemo(() => validateInteractionTopics(currentTopics), [currentTopics]);
  const isDirty = useMemo(() => {
    const saved = savedSnapshot.current[department];
    if (!topicsByDept || !saved) return false;
    return !topicsEqual(currentTopics, saved);
  }, [topicsByDept, currentTopics, department]);

  // Per-field unsaved changes for blue changed-text. Keyed by current edit
  // indexes so the editor can tint exactly the edited names/descriptions.
  const changedFields: ChangedTopicFields = useMemo(() => {
    const saved = savedSnapshot.current[department];
    if (!topicsByDept || !saved) return {};
    return diffChangedTopics(saved, currentTopics);
  }, [topicsByDept, currentTopics, department]);

  // Any department with unsaved edits — guards tab close/refresh so explicit
  // saves never lose work silently. In-app navigation keeps per-department
  // edits in memory; only a full unload can drop them.
  const anyDirty = useMemo(() => {
    if (!topicsByDept) return false;
    return Object.entries(topicsByDept).some(([dept, topics]) => {
      const saved = savedSnapshot.current[dept];
      return saved ? !topicsEqual(topics, saved) : false;
    });
  }, [topicsByDept]);

  useEffect(() => {
    if (!anyDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Required alongside preventDefault for a reliable native prompt.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [anyDirty]);

  // In-app navigation unmounts the page (React Router doesn't fire
  // beforeunload), so dirty drafts get a confirmation dialog. Department
  // pills stay unblocked — same path, drafts kept in memory by design.
  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) => anyDirty && currentLocation.pathname !== nextLocation.pathname
  );

  const saveTopicsInner = useCallback(
    async (dept: string, topics: InteractionTopicInput[]) => {
      const epochAtSend = saveEpoch.current;
      clearError();
      try {
        const variables: UpdateInteractionTopicConfigVariables = {
          input: {
            department: dept,
            topics: topics.map(toSavePayload),
            lockVersion: latestLockVersions.current[dept] ?? 0
          }
        };
        const result = await updateTopics({ variables });
        const payload = result.data?.updateInteractionTopicConfig;
        if (payload?.success && payload.config) {
          const prevSnapshot = savedSnapshot.current[dept] ?? [];
          const savedTopics = payload.config.topics.map(toEditableTopic);
          savedSnapshot.current[dept] = savedTopics;
          // Sync the ref immediately: a chained follow-up save reads it in the
          // same tick, before React re-renders with the new state.
          const nextVersion = payload.config?.lockVersion ?? 0;
          latestLockVersions.current = { ...latestLockVersions.current, [dept]: nextVersion };
          setLockVersions((prev) => ({ ...prev, [dept]: nextVersion }));
          // Only sync the server list into edit state when nothing changed
          // mid-flight; otherwise keep the newer local edits (the save button
          // stays visible). The merge keeps local whitespace (in-progress
          // blank rows) wherever content matches.
          // The flash fires only on a fully synced save — never while a
          // follow-up save is still due — and lands on exactly the fields
          // this save persisted.
          if (saveEpoch.current === epochAtSend) {
            setTopicsByDept((prev) =>
              prev ? { ...prev, [dept]: mergeEchoTopics(prev[dept] ?? [], savedTopics) } : prev
            );
            // The bar unmounts on a fully synced save: return keyboard focus
            // to the editor when it was inside the bar and this department
            // is still showing.
            if (saveFocusDept.current === dept && latestDepartment.current === dept) {
              setFocusKey(topicFocusKey(0));
            }
            saveFocusDept.current = null;
            const changes = diffSavedTopics(prevSnapshot, variables.input.topics);
            // A delete-only save has no remaining field to badge, so it gets
            // the page-level toast instead.
            const deleted = variables.input.topics.length < prevSnapshot.length;
            if (Object.keys(changes).length > 0 || deleted) {
              setFieldFlash((prev) => ({ key: (prev?.key ?? 0) + 1, dept, changes, deleted }));
              if (flashTimer.current) clearTimeout(flashTimer.current);
              flashTimer.current = setTimeout(() => setFieldFlash(null), SAVE_FLASH_MS);
            }
          }
        } else if (payload && !payload.success && /another admin saved/i.test(payload.message ?? "")) {
          // Version conflict: another admin saved this department first.
          // Reload the server list and install it directly from the refetch
          // result (the conflicting local edits are discarded — the banner
          // says so) so the next save carries a fresh version. Installing
          // here instead of a data effect keeps recovery atomic with the
          // request that triggered it: no stale-data installs on later
          // keystrokes, no stuck reloading state when the cache object is
          // unchanged.
          setFieldFlash(null);
          const deptLabel = INTERACTION_TOPIC_DEPARTMENTS.find((candidate) => candidate.value === dept)?.label ?? dept;
          setError(
            `${payload.message ?? "Couldn't save interaction topics."} Loading the latest ${deptLabel} topics — conflicting edits were discarded.`
          );
          setReloadingDept(dept);
          try {
            const reload = await refetch();
            const config = reload.data?.interactionTopicConfigs.find((candidate) => candidate.department === dept);
            if (!config) {
              setError("Couldn't load the latest topics. Reload the page and try again.");
              return;
            }
            const serverTopics = config.topics.map(toEditableTopic);
            savedSnapshot.current[dept] = serverTopics;
            setTopicsByDept((prev) => (prev ? { ...prev, [dept]: serverTopics } : prev));
            setLockVersions((prev) => ({ ...prev, [dept]: config.lockVersion ?? 0 }));
          } catch {
            setError("Couldn't load the latest topics after a conflicting save. Save again to retry.");
            return;
          } finally {
            setReloadingDept(null);
          }
        } else {
          setError(payload?.message || "Couldn't save interaction topics.");
        }
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Couldn't save interaction topics.");
      }
    },
    [updateTopics, setError, clearError, refetch]
  );

  // Queues saves behind each other so a follow-up save waits for the previous
  // echo (and its lock version) instead of racing it into a false conflict.
  // A rejection never breaks the chain for later saves.
  const saveTopics = useCallback(
    (dept: string, topics: InteractionTopicInput[]) => {
      const run = saveChainRef.current.then(() => saveTopicsInner(dept, topics));
      saveChainRef.current = run.catch(() => {});
      return run;
    },
    [saveTopicsInner]
  );

  const handleSave = useCallback(() => {
    if (savingDept !== null || reloadingDept === department || !isDirty || hasValidationErrors(errors)) return;
    saveFocusDept.current = (saveBarRef.current?.contains(document.activeElement) ?? false) ? department : null;
    setSavingDept(department);
    void saveTopics(department, currentTopics).finally(() => setSavingDept(null));
  }, [savingDept, reloadingDept, isDirty, errors, saveTopics, department, currentTopics]);

  // Restores the saved snapshot for the current department, dropping unsaved
  // edits. Blocked while this department is saving or reloading (another
  // department's in-flight work touches separate state); other departments'
  // in-memory edits are untouched. Cancels any in-flight suggestion so a
  // late fill can't resurrect dirtiness, and returns focus to the editor
  // since the bar unmounts.
  const handleDiscard = useCallback(() => {
    if (savingDept === department || reloadingDept === department || !isDirty) return;
    const saved = savedSnapshot.current[department];
    if (!saved) return;
    const hadFocus = saveBarRef.current?.contains(document.activeElement) ?? false;
    setTopicsByDept((prev) => (prev ? { ...prev, [department]: saved } : prev));
    setFieldFlash(null);
    setSuggestingKey(null);
    suggestingRef.current = null;
    if (hadFocus) setFocusKey(topicFocusKey(0));
  }, [savingDept, reloadingDept, isDirty, department]);

  const handleDeptChange = useCallback(
    (value: string | null) => {
      if (!value || value === department) return;
      // Explicit saves only: switching departments keeps unsaved edits in
      // memory per department — nothing saves automatically.
      setAnalysis(null);
      setAnalyzedDept(null);
      // Drop in-flight request attribution: a resolving suggestion/review for
      // the old department must not touch the new one (the dept checks also
      // guard this; clearing here re-enables controls immediately).
      setSuggestingKey(null);
      suggestingRef.current = null;
      analysisEpoch.current += 1;
      analyzingRef.current = null;
      setAnalyzing(false);
      saveFocusDept.current = null;
      addSearchParam(INTERACTION_TOPIC_DEPARTMENT_PARAM, value);
    },
    [department, addSearchParam]
  );

  const updateDeptTopics = useCallback(
    (dept: string, updater: (topics: InteractionTopicInput[]) => InteractionTopicInput[]) => {
      saveEpoch.current += 1;
      setTopicsByDept((prev) => {
        if (!prev) return prev;
        return { ...prev, [dept]: updater(prev[dept] ?? []) };
      });
    },
    []
  );

  const handleChangeTopicName = useCallback(
    (topicIndex: number, name: string) => {
      updateDeptTopics(department, (topics) => {
        if (isTopicLocked(topics, topicIndex)) return topics;
        return topics.map((topic, index) => (index === topicIndex ? { ...topic, name } : topic));
      });
    },
    [department, updateDeptTopics]
  );

  const handleChangeTopicDescription = useCallback(
    (topicIndex: number, description: string) => {
      updateDeptTopics(department, (topics) => {
        if (isTopicLocked(topics, topicIndex)) return topics;
        return topics.map((topic, index) => (index === topicIndex ? { ...topic, description } : topic));
      });
    },
    [department, updateDeptTopics]
  );

  const handleChangeSubtopicName = useCallback(
    (topicIndex: number, subtopicIndex: number, name: string) => {
      updateDeptTopics(department, (topics) =>
        topics.map((topic, index) => {
          if (index !== topicIndex) return topic;
          return {
            ...topic,
            subtopics: topic.subtopics.map((sub, subIndex) => (subIndex === subtopicIndex ? { ...sub, name } : sub))
          };
        })
      );
    },
    [department, updateDeptTopics]
  );

  const handleChangeSubtopicDescription = useCallback(
    (topicIndex: number, subtopicIndex: number, description: string) => {
      updateDeptTopics(department, (topics) =>
        topics.map((topic, index) => {
          if (index !== topicIndex) return topic;
          return {
            ...topic,
            subtopics: topic.subtopics.map((sub, subIndex) =>
              subIndex === subtopicIndex ? { ...sub, description } : sub
            )
          };
        })
      );
    },
    [department, updateDeptTopics]
  );

  const handleAddSubtopic = useCallback(
    (topicIndex: number) => {
      updateDeptTopics(department, (topics) =>
        topics.map((topic, index) =>
          index === topicIndex
            ? { ...topic, subtopics: [...topic.subtopics, { name: "", description: "", key: createRowKey("sub") }] }
            : topic
        )
      );
    },
    [department, updateDeptTopics]
  );

  const handleRemoveSubtopic = useCallback(
    (topicIndex: number, subtopicIndex: number) => {
      updateDeptTopics(department, (topics) =>
        topics.map((topic, index) => {
          if (index !== topicIndex) return topic;
          return { ...topic, subtopics: topic.subtopics.filter((_, subIndex) => subIndex !== subtopicIndex) };
        })
      );
    },
    [department, updateDeptTopics]
  );

  const handleAddTopic = useCallback(() => {
    const topics = topicsByDept?.[department] ?? [];
    const insertAt = insertionIndexBeforeOther(topics, (topic) => isOtherName(topic.name));
    updateDeptTopics(department, (current) =>
      insertBeforeOther(
        current,
        { name: "", description: "", subtopics: [], code: null, defaultPrivate: null, key: createRowKey("topic") },
        (topic) => isOtherName(topic.name)
      )
    );
    setFocusKey(topicFocusKey(insertAt));
  }, [department, topicsByDept, updateDeptTopics]);

  const handleRemoveTopic = useCallback(
    (topicIndex: number) => {
      updateDeptTopics(department, (topics) => {
        if (isTopicLocked(topics, topicIndex)) return topics;
        return topics.filter((_, index) => index !== topicIndex);
      });
    },
    [department, updateDeptTopics]
  );

  const handleSuggestDescription = useCallback(
    async (topicIndex: number, subtopicIndex: number | null) => {
      // Single-flight: one suggestion at a time across all rows.
      if (suggestingRef.current !== null) return;
      const topics = latestTopics.current?.[latestDepartment.current] ?? [];
      const topic = topics[topicIndex];
      if (!topic || !topic.name.trim()) return;
      const subtopic = subtopicIndex === null ? null : topic.subtopics[subtopicIndex];
      if (subtopicIndex !== null && !subtopic?.name.trim()) return;
      // Capture stable row identity plus the live description: rows may shift
      // while the request is in flight, and the admin may keep typing — the
      // fill resolves by key and never clobbers newer text.
      const topicKey = topic.key;
      const subKey = subtopic?.key;
      const sentDescription = (subtopic ? subtopic.description : topic.description) ?? "";
      const key = suggestionKeyFor(topicKey, subKey);
      const requestDept = latestDepartment.current;
      suggestingRef.current = key;
      setSuggestingKey(key);
      clearError();
      try {
        const result = await suggestDescription({
          variables: {
            department: requestDept,
            topic: topic.name.trim(),
            ...(subtopic ? { subtopic: subtopic.name.trim() } : {})
          }
        });
        const payload = result.data?.suggestInteractionTopicDescription;
        if (latestDepartment.current !== requestDept) return;
        if (suggestingRef.current !== key) return;
        if (payload && payload.configured && payload.description?.trim()) {
          const suggestion = payload.description.trim();
          updateDeptTopics(requestDept, (current) => {
            const targetIndex = current.findIndex((entry) => entry.key !== undefined && entry.key === topicKey);
            if (targetIndex === -1) return current;
            if (subtopicIndex === null) {
              if ((current[targetIndex].description ?? "") !== sentDescription) return current;
              return current.map((entry, index) =>
                index === targetIndex ? { ...entry, description: suggestion } : entry
              );
            }
            const targetSubIndex = current[targetIndex].subtopics.findIndex(
              (sub) => sub.key !== undefined && sub.key === subKey
            );
            if (targetSubIndex === -1) return current;
            if ((current[targetIndex].subtopics[targetSubIndex].description ?? "") !== sentDescription) return current;
            return current.map((entry, index) => {
              if (index !== targetIndex) return entry;
              return {
                ...entry,
                subtopics: entry.subtopics.map((sub, subIndex) =>
                  subIndex === targetSubIndex ? { ...sub, description: suggestion } : sub
                )
              };
            });
          });
        } else if (payload && !payload.configured) {
          setError("AI suggestions aren't available right now. Try again later.");
        } else {
          setError("Couldn't generate a description. Try again.");
        }
      } catch (caught) {
        // Suppress errors from superseded requests so a stale failure never
        // banners the UI after a department switch or newer request.
        if (latestDepartment.current !== requestDept || suggestingRef.current !== key) return;
        setError(caught instanceof Error ? caught.message : "Couldn't generate a description.");
      } finally {
        if (suggestingRef.current === key) suggestingRef.current = null;
        setSuggestingKey((prev) => (prev === key ? null : prev));
      }
    },
    [suggestDescription, updateDeptTopics, setError, clearError]
  );

  const handleAnalyze = useCallback(async () => {
    if (analyzingRef.current !== null) return;
    clearError();
    const requestDept = latestDepartment.current;
    analysisEpoch.current += 1;
    const requestEpoch = analysisEpoch.current;
    analyzingRef.current = requestEpoch;
    setAnalyzing(true);
    // Serialize the admin's current on-screen settings (including unsaved
    // edits) so the LLM judges what is actually on screen — never the stale
    // saved row. Read-only: findings are visibility, nothing auto-applies.
    const serialized = (latestTopics.current?.[requestDept] ?? []).map(toSavePayload);
    try {
      const result = await runAnalysisQuery({ variables: { department: requestDept, topics: serialized } });
      const payload = result.data?.analyzeInteractionTopics;
      if (!payload) {
        if (latestDepartment.current !== requestDept || requestEpoch !== analysisEpoch.current) return;
        setError("Topic review is unavailable right now. Try again.");
        return;
      }
      if (latestDepartment.current !== requestDept) return;
      if (requestEpoch !== analysisEpoch.current) return;
      setAnalysis({ summary: payload.summary, findings: payload.findings ?? [], configured: payload.configured });
      setAnalyzedDept(requestDept);
      if (!payload.configured) {
        setError("AI review isn't available right now. Try again later.");
      }
    } catch (caught) {
      if (latestDepartment.current !== requestDept || requestEpoch !== analysisEpoch.current) return;
      setError(caught instanceof Error ? caught.message : "Topic review is unavailable right now. Try again.");
    } finally {
      if (analyzingRef.current === requestEpoch) analyzingRef.current = null;
      if (requestEpoch === analysisEpoch.current) setAnalyzing(false);
    }
  }, [runAnalysisQuery, setError, clearError]);

  const handleFocusApplied = useCallback(() => {
    setFocusKey(null);
  }, []);

  const visibleFindings = analyzedDept === department && analysis?.configured ? analysis.findings : [];

  const saveBlocked = hasValidationErrors(errors);

  return (
    <TabLayout
      stickyHeader={
        <div className="mt-6 flex flex-col gap-3">
          <SegmentedButton
            ariaLabel="Department"
            options={visibleDepartments.map((dept) => ({
              label: dept.label,
              value: dept.value
            }))}
            value={department}
            onChange={handleDeptChange}
            fullWidth
          />
          {isDirty ? (
            <div
              ref={saveBarRef}
              data-testid="topics-save-bar"
              className="flex items-center justify-between gap-3 rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface-container-low px-4 py-2"
            >
              <span className="text-body-medium text-sys-brand-on-surface-variant">
                {reloadingDept === department
                  ? "Loading the latest topics…"
                  : saveBlocked
                    ? "Fix the errors to save."
                    : "You have unsaved changes."}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="text"
                  disabled={savingDept === department || reloadingDept === department}
                  onClick={handleDiscard}
                  testId="discard-topics"
                >
                  Discard
                </Button>
                <Button
                  variant="filled"
                  loading={savingDept === department}
                  disabled={saveBlocked || savingDept !== null || reloadingDept === department}
                  onClick={handleSave}
                  testId="save-topics"
                >
                  Save changes
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      }
    >
      <div className="pt-12" />
      {error && !topicsByDept ? (
        <ConfigurationLoadError onRetry={() => refetch()} />
      ) : loading || !topicsByDept ? (
        <div className="flex items-center gap-3" data-testid="interaction-topics-loading">
          <Progress size={20} />
          <span className="text-body-medium text-sys-brand-on-surface-variant">Loading topics...</span>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <style>
            {`@keyframes topics-save-pop {
  0% { opacity: 0; transform: scale(0.85) translateY(4px); }
  18% { opacity: 1; transform: scale(1.04) translateY(0); }
  30% { opacity: 1; transform: scale(1); }
  78% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.97); }
}
.topics-save-flash { animation: topics-save-pop 1.8s ease forwards; }
/* Unsaved edits render input text in brand blue (label, outline, and error
   colors are untouched — blue borders would read as focus). */
.topics-changed-field { --md-outlined-text-field-input-text-color: var(--md-sys-color-primary); }`}
          </style>
          <TopicQualityPanel
            analysis={analyzedDept === department ? analysis : null}
            analyzing={analyzing}
            isDirty={isDirty}
            onAnalyze={handleAnalyze}
          />
          <TopicsEditor
            topics={currentTopics}
            errors={errors}
            focusKey={focusKey}
            flash={fieldFlash?.dept === department ? fieldFlash : null}
            changed={changedFields}
            findings={visibleFindings}
            suggestingKey={suggestingKey}
            onFocusApplied={handleFocusApplied}
            onChangeTopicName={handleChangeTopicName}
            onChangeTopicDescription={handleChangeTopicDescription}
            onChangeSubtopicName={handleChangeSubtopicName}
            onChangeSubtopicDescription={handleChangeSubtopicDescription}
            onAddSubtopic={handleAddSubtopic}
            onRemoveSubtopic={handleRemoveSubtopic}
            onSuggestDescription={handleSuggestDescription}
            onAddTopic={handleAddTopic}
            onRemoveTopic={handleRemoveTopic}
          />
          {fieldFlash?.dept === department && fieldFlash.deleted ? (
            <div
              key={fieldFlash.key}
              role="status"
              data-testid="topics-delete-flash"
              className="topics-save-flash fixed bottom-6 left-0 right-0 z-[60] mx-auto w-fit rounded-full border border-sys-brand-outline-variant bg-sys-brand-surface px-3 py-1.5 text-body-small font-medium text-sys-brand-primary shadow-sm"
            >
              ✓ Changes saved
            </div>
          ) : null}
        </div>
      )}
      {navigationBlocker.state === "blocked" ? (
        <Dialog
          open
          onClosed={() => {
            if (navigationBlocker.state === "blocked") navigationBlocker.reset();
          }}
          headline="Discard unsaved changes?"
          actions={
            <>
              <Button variant="text" onClick={() => navigationBlocker.reset()} testId="nav-guard-keep-editing">
                Keep editing
              </Button>
              <Button theme="error" onClick={() => navigationBlocker.proceed()} testId="nav-guard-discard">
                Discard changes
              </Button>
            </>
          }
        >
          <p>You have unsaved topic changes. Leaving this page will discard them.</p>
        </Dialog>
      ) : null}
    </TabLayout>
  );
}
